import React, { useState } from 'react';
import classnames from 'classnames';

import { getProviderNameFriendly } from '../../utils/provider';
import { AppointmentTimeView } from './AppointmentTimeView';
import { CheckMark } from '../../components/CheckMark';
import ProviderImage from '../../components/ProviderImage';
import { AppointmentLocationView } from './AppointmentLocationView';
import { PhoneNumberView } from './PhoneNumberView';

import * as api from '../../api';

import styles from './index.module.css';

interface AppointmentInformation {
  id: string;
  startTime: Date;
  provider: api.User.SafeProvider;
  arrivalTime?: Date;
  practice: Omit<api.Practice.SafePractice, 'zip' | 'name'> & {
    name?: string;
  };
  status?: api.EhrTimeSlot.AppointmentStatus;
  virtualVisit?: boolean;
  hideEncounterStartTimeByVisitKind?: string[];
  kindOfVisit?: string;
}

interface Props extends React.PropsWithChildren<AppointmentInformation> {
  selectable?: boolean;
  onSelect?: (appointmentId: string) => void;
  onDeselect?: (appointmentId: string) => void;
  displayPhoneNumber?: boolean;
  reschedule?: boolean;
  badge?: JSX.Element;
  extraInfoContent?: JSX.Element;
  actionInfoContext?: JSX.Element;
}

/**
 * Display the doctor profile for a given appointment
 */
export const DoctorAppointmentProfileView: React.FC<Props> = ({
  id,
  startTime,
  arrivalTime,
  status,
  selectable,
  onSelect,
  onDeselect,
  provider,
  practice,
  virtualVisit,
  badge,
  extraInfoContent,
  actionInfoContext: actionInfoContetx,
  displayPhoneNumber = true,
  children,
  hideEncounterStartTimeByVisitKind = [],
  kindOfVisit,
}) => {
  const displayTime = kindOfVisit
    ? !hideEncounterStartTimeByVisitKind.includes(kindOfVisit)
    : true;
  const providerFullName = getProviderNameFriendly(provider, [
    'firstName',
    'lastName',
    'suffix',
  ]);
  const [selected, setSelected] = useState(false);
  const borderStyle = selected
    ? styles.BorderSelected
    : styles.BorderUnSelected;
  return (
    <div
      className={classnames(
        styles.SelectableAppointmentInformationContainer,
        borderStyle,
      )}
    >
      <div className={styles.ProfileContainer}>
        {selectable && (
          <div className={styles.CheckMarkContainer}>
            <CheckMark
              checked={selected}
              onClick={() => {
                selected
                  ? onDeselect && onDeselect(id)
                  : onSelect && onSelect(id);
                setSelected(!selected);
              }}
            />
          </div>
        )}
        <div className={styles.AppointmentInformationContainer}>
          {badge}
          <AppointmentTimeView
            startTime={startTime}
            status={status}
            timezone={practice.timezone}
            arrivalTime={arrivalTime}
            displayTime={displayTime}
          />
          <div className={styles.ProviderAndLocation}>
            <div className={styles.ProviderImageContainer}>
              <ProviderImage
                className={styles.ProviderImage}
                provider={provider}
                size={'Small'}
              />
            </div>
            <div className={styles.ProviderInfoContainer}>
              <div className={styles.ProviderName}>{providerFullName}</div>
              <AppointmentLocationView
                practice={practice}
                virtualVisit={virtualVisit}
              />
              {displayPhoneNumber && (
                <PhoneNumberView
                  phoneNumber={practice.phoneNumber}
                  className={styles.PhoneNumber}
                />
              )}
              {extraInfoContent}
            </div>
          </div>
          {actionInfoContetx}
        </div>
      </div>

      {children}
    </div>
  );
};
